// Transaction endpoints + the client-side category-summary aggregation.

import { categoryOrUncat } from '../category';
import { apiFetch } from './client';
import type { Transaction, Split, CategorySummary } from './types';

// Query transactions for a specific month
export async function getTransactionsForMonth(yearMonth: string): Promise<Transaction[]> {
	return apiFetch<Transaction[]>(`/transactions?month=${yearMonth}`);
}

// Update transaction category
export async function updateTransactionCategory(
	pk: string,
	sk: string,
	category: string
): Promise<void> {
	// URL encode pk and sk for path parameters
	const encodedPk = encodeURIComponent(pk);
	const encodedSk = encodeURIComponent(sk);

	await apiFetch(`/transactions/${encodedPk}/${encodedSk}`, {
		method: 'PATCH',
		body: JSON.stringify({ category }),
	});
}

// Mark/unmark transaction as excluded from budget
export async function setTransactionExcludeFromBudget(
	pk: string,
	sk: string,
	excludeFromBudget: boolean
): Promise<void> {
	const encodedPk = encodeURIComponent(pk);
	const encodedSk = encodeURIComponent(sk);

	await apiFetch(`/transactions/${encodedPk}/${encodedSk}`, {
		method: 'PATCH',
		body: JSON.stringify({ excludeFromBudget }),
	});
}

/**
 * Shift a transaction's effective pay-cycle by ±1 cycle from its date-derived
 * month, or restore it (offset=0). The row stays at its derived partition;
 * the API merges adjacent partitions on read.
 */
export async function setTransactionMonthOffset(
	pk: string,
	sk: string,
	monthOffset: -1 | 0 | 1
): Promise<void> {
	const encodedPk = encodeURIComponent(pk);
	const encodedSk = encodeURIComponent(sk);

	await apiFetch(`/transactions/${encodedPk}/${encodedSk}`, {
		method: 'PATCH',
		body: JSON.stringify({ monthOffset }),
	});
}

/**
 * Split a transaction across multiple categories. `splits` must have ≥2 lines
 * whose amounts share the transaction's sign and sum to its amount; the API
 * validates this and sets the top-level category to the "SPLIT" sentinel.
 */
export async function setTransactionSplits(
	pk: string,
	sk: string,
	splits: Split[]
): Promise<void> {
	const encodedPk = encodeURIComponent(pk);
	const encodedSk = encodeURIComponent(sk);

	await apiFetch(`/transactions/${encodedPk}/${encodedSk}`, {
		method: 'PATCH',
		body: JSON.stringify({ splits }),
	});
}

// Remove a transaction's splits and restore a single category (default UNCAT).
export async function clearTransactionSplits(
	pk: string,
	sk: string,
	category = 'UNCAT'
): Promise<void> {
	const encodedPk = encodeURIComponent(pk);
	const encodedSk = encodeURIComponent(sk);

	await apiFetch(`/transactions/${encodedPk}/${encodedSk}`, {
		method: 'PATCH',
		body: JSON.stringify({ splits: [], category }),
	});
}

// Calculate category summary from transactions (client-side).
// Excludes transfers and budget-excluded transactions by default. The
// optional `excludeAccountIds` skips any transaction whose `accountId` is in
// the set — used by the dashboard to drop credit-card account activity from
// budget aggregation, since cash spend (and the per-card-budget category)
// belongs to bank-side outflows, not to card-side charges/credits.
export function calculateCategorySummary(
	transactions: Transaction[],
	excludeTransfers = true,
	excludeBudgetExcluded = true,
	excludeAccountIds?: ReadonlySet<string>
): CategorySummary[] {
	const summaryMap = new Map<string, { total: number; count: number }>();

	for (const tx of transactions) {
		// Skip transfers if excluding
		if (excludeTransfers && tx.isTransfer) continue;
		// Skip budget-excluded transactions if excluding
		if (excludeBudgetExcluded && tx.excludeFromBudget) continue;
		// Skip transactions on accounts the caller wants excluded entirely
		// (e.g. credit-card accounts on the dashboard).
		if (excludeAccountIds && tx.accountId && excludeAccountIds.has(tx.accountId)) continue;

		// A split transaction attributes its amount across its split lines; the
		// top-level category/amount are ignored. Per-line excludeFromBudget skips
		// money-movement lines (e.g. the transfer/principal portion of a debt
		// payment) while the counted (cost) lines still contribute. A debt payment
		// that carries a cost is stored with is_transfer cleared, so its split is
		// reached here.
		if (tx.splits && tx.splits.length > 0) {
			for (const split of tx.splits) {
				if (excludeBudgetExcluded && split.excludeFromBudget) continue;
				const cat = categoryOrUncat(split.category);
				const e = summaryMap.get(cat) || { total: 0, count: 0 };
				e.total += split.amount;
				e.count += 1;
				summaryMap.set(cat, e);
			}
			continue;
		}

		const category = categoryOrUncat(tx.category);
		const existing = summaryMap.get(category) || { total: 0, count: 0 };
		existing.total += tx.amount;
		existing.count += 1;
		summaryMap.set(category, existing);
	}

	return Array.from(summaryMap.entries())
		.map(([category, { total, count }]) => ({
			category,
			total,
			count,
		}))
		.sort((a, b) => a.total - b.total); // Sort by total (expenses first, then income)
}
